import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, cn } from '@mf-tools/domains-ui';
import { Briefcase, ShieldCheck, Sparkles, Wallet } from 'lucide-react';
import { Container } from '../components';

export interface FeaturesProps {
  className?: string;
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  bullets: string[];
}

function FeatureCard({ icon, title, description, bullets }: FeatureCardProps): React.ReactElement {
  return (
    <Card className="transition-shadow hover:shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
            {icon}
          </span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{description}</p>
        <ul className="grid gap-2 text-sm text-muted-foreground">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
              {b}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function Features({ className }: FeaturesProps): React.ReactElement {
  return (
    <section id="features" className={cn('py-14 sm:py-20', className)} aria-labelledby="features-title">
      <Container>
        <div className="space-y-10">
          <div className="max-w-3xl space-y-3">
            <h2 id="features-title" className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Product value, in one place
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
              Standardize the work that normally lives across five tools, three spreadsheets, and a dozen Slack threads.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <FeatureCard
              icon={<Briefcase className="h-4 w-4" aria-hidden="true" />}
              title="People Ops + Hiring"
              description="Hiring workflows, onboarding, payroll readiness"
              bullets={['Structured hiring pipeline', 'Onboarding checklists and owners', 'Policy-aware workflows']}
            />
            <FeatureCard
              icon={<Wallet className="h-4 w-4" aria-hidden="true" />}
              title="Finance + Admin"
              description="Payments, bookkeeping readiness, cash visibility"
              bullets={['Spend visibility and approvals', 'Receipt capture and categorization', 'Monthly close readiness']}
            />
            <FeatureCard
              icon={<ShieldCheck className="h-4 w-4" aria-hidden="true" />}
              title="Security + GRC"
              description="Policies, access controls, SOC2/ISO readiness workflows"
              bullets={['Policy library and attestations', 'Access controls and audit trail', 'Evidence workflows by control']}
            />
            <FeatureCard
              icon={<Sparkles className="h-4 w-4" aria-hidden="true" />}
              title="Founder Command Center"
              description="One dashboard for operational status, tasks, approvals, audit trail"
              bullets={['One view of what’s blocked', 'Approvals and accountability', 'Enterprise-ready reporting']}
            />
          </div>
        </div>
      </Container>
    </section>
  );
}

